"use client";

// The person's identities and what each agent, connection and key mixes, for
// any place that offers a choice of them (the make step, a connection's name, a
// key's mint form, an agent's settings). (What a person reads says identities; this
// file and its names keep the old word, personalities, so nothing breaks.) `open` is false when identities are
// not open to the person (the routes answer 404, F.agentIdentity): every such
// place then shows nothing at all. A read that does not answer is asked again
// quietly; a choice an earlier page could not save is written first.

import { useCallback, useEffect, useState } from "react";
import { applyPendingMixes, backoff, getMix, getPersonalities, type MixState } from "../../lib/identity/client";
import type { AgentIdentity } from "../../lib/identity/types";

export interface PersonalitiesState {
  /** still reading (nothing is drawn yet) */
  loading: boolean;
  open: boolean;
  list: AgentIdentity[];
  mix: MixState | null;
}

export function usePersonalities(): PersonalitiesState & { reload: () => void; setList: (next: AgentIdentity[]) => void } {
  const [state, setState] = useState<PersonalitiesState>({ loading: true, open: false, list: [], mix: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    let timer: number | null = null;
    const read = async (attempt: number): Promise<void> => {
      const p = await getPersonalities();
      if (!alive) return;
      if (!p) {
        // not answered: ask again in a little while, saying nothing
        if (attempt < 6) timer = window.setTimeout(() => void read(attempt + 1), backoff(attempt));
        return;
      }
      if (!p.open) {
        setState({ loading: false, open: false, list: [], mix: null });
        return;
      }
      void applyPendingMixes();
      const mix = await getMix();
      if (!alive) return;
      setState({ loading: false, open: true, list: p.list, mix: mix ?? { ready: false, mix: {}, places: {} } });
    };
    void read(0);
    return () => {
      alive = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [tick]);

  const setList = useCallback((next: AgentIdentity[]) => setState((s) => ({ ...s, list: next })), []);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { ...state, reload, setList };
}
